import type { DataTableColumnDef } from '../components/table/dataTableTypes';
import type {
  TableId,
  TableTotalsColumnSpec,
  TableTotalsFormat,
  TableTotalsTableConfig,
} from '../constants/tableTotalsConfig';
import { getTableTotalsConfig, isTableTotalsEnabled } from '../constants/tableTotalsConfig';

export type ComputedColumnTotal = {
  columnId: string;
  value: number;
  spec: TableTotalsColumnSpec;
};

export type TableTotalsResult = {
  footerLabel: string;
  labelColumnId: string;
  totalsByColumnId: Map<string, ComputedColumnTotal>;
};

function readRowNumber(
  row: Record<string, unknown>,
  columnId: string,
  spec: TableTotalsColumnSpec,
): number {
  if (spec.getValue) {
    const v = spec.getValue(row, columnId);
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  const key = spec.valueKey ?? columnId;
  const raw = row[key];
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function resolveSpecForColumn(
  columnId: string,
  config: TableTotalsTableConfig,
): TableTotalsColumnSpec | undefined {
  if (config.columns?.[columnId]) return config.columns[columnId];
  for (const dyn of config.dynamicColumns ?? []) {
    if (dyn.pattern.test(columnId)) {
      return {
        aggregate: dyn.aggregate,
        format: dyn.format,
        getValue: dyn.getValue,
      };
    }
  }
  return undefined;
}

export function computeTableTotals<TData extends Record<string, unknown>>(
  tableId: TableId | undefined,
  rows: TData[],
  visibleColumnIds: string[],
): TableTotalsResult | null {
  if (!tableId || !isTableTotalsEnabled(tableId) || rows.length === 0) return null;
  const config = getTableTotalsConfig(tableId);
  if (!config) return null;

  const footerLabel = config.footerLabel ?? 'Total';
  const labelColumnId =
    config.labelColumnId && visibleColumnIds.includes(config.labelColumnId)
      ? config.labelColumnId
      : visibleColumnIds[0] ?? '';

  const totalsByColumnId = new Map<string, ComputedColumnTotal>();
  const sums = new Map<string, { sum: number; count: number; spec: TableTotalsColumnSpec }>();

  for (const columnId of visibleColumnIds) {
    const spec = resolveSpecForColumn(columnId, config);
    if (!spec) continue;
    sums.set(columnId, { sum: 0, count: 0, spec });
  }

  if (sums.size === 0) return null;

  for (const row of rows) {
    sums.forEach((acc, columnId) => {
      const v = readRowNumber(row, columnId, acc.spec);
      acc.sum += v;
      acc.count += 1;
    });
  }

  sums.forEach((acc, columnId) => {
    const value =
      acc.spec.aggregate === 'avg' && acc.count > 0 ? acc.sum / acc.count : acc.sum;
    totalsByColumnId.set(columnId, { columnId, value, spec: acc.spec });
  });

  return { footerLabel, labelColumnId, totalsByColumnId };
}

export function getVisibleDataTableColumnIds<TData>(
  columns: DataTableColumnDef<TData>[],
): string[] {
  return columns.map((c) => c.id);
}

export { isTableTotalsEnabled, getTableTotalsConfig };
export type { TableTotalsFormat };
